import { ref } from "vue";

export const PIN_MAX_ATTEMPTS = 5;
export const PIN_LOCKOUT_MS = 30 * 1000;

const isOpen = ref(false);
const value = ref("");
const attemptsLeft = ref(PIN_MAX_ATTEMPTS);
const lockedUntil = ref(0);
const secondsLeft = ref(0);
const status = ref("idle"); // 'idle' | 'locked'
const justFailed = ref(false);

let verifyFn = null;
let subjectKey = "";
let activeMaxAttempts = PIN_MAX_ATTEMPTS;
let activeLockoutMs = PIN_LOCKOUT_MS;
let resultResolver = null;
let countdownTimer = null;

function storageKey(subject) {
  return `__avocash_pin_auth_${subject || "anon"}`;
}

function persistState() {
  if (!subjectKey) return;
  try {
    window.__piniaLcStorage?.set(storageKey(subjectKey), {
      attemptsLeft: attemptsLeft.value,
      lockedUntil: lockedUntil.value,
      updatedAt: Date.now(),
    });
  } catch (error) {
    console.error("Failed to persist PIN auth state", error);
  }
}

function loadPersistedState(subject) {
  if (!subject) return false;
  try {
    const saved = window.__piniaLcStorage?.get(storageKey(subject));
    if (saved && Number.isFinite(saved.attemptsLeft)) {
      attemptsLeft.value = Math.max(0, Math.min(PIN_MAX_ATTEMPTS, Number(saved.attemptsLeft)));
      lockedUntil.value = Number(saved.lockedUntil) || 0;
      return true;
    }
  } catch (error) {
    // ignore malformed state
  }
  return false;
}

function clearCountdown() {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
}

function startCountdown() {
  clearCountdown();
  const tick = () => {
    const remaining = Math.max(0, Math.ceil((lockedUntil.value - Date.now()) / 1000));
    secondsLeft.value = remaining;
    if (remaining <= 0) {
      clearCountdown();
      attemptsLeft.value = activeMaxAttempts;
      lockedUntil.value = 0;
      status.value = "idle";
      justFailed.value = false;
      value.value = "";
      persistState();
    }
  };
  tick();
  countdownTimer = setInterval(tick, 1000);
}

function resolveOutcome(outcome) {
  if (resultResolver) {
    const resolve = resultResolver;
    resultResolver = null;
    resolve(outcome);
  }
}

function closeWith(outcome) {
  clearCountdown();
  value.value = "";
  justFailed.value = false;
  status.value = "idle";
  isOpen.value = false;
  resolveOutcome(outcome);
}

export function useTransactAuthNumpad() {
  function open(options = {}) {
    const { verify = null, subject = "", maxAttempts = PIN_MAX_ATTEMPTS, lockoutMs = PIN_LOCKOUT_MS } = options;

    // A previous flow may still be pending; resolve it as cancelled first.
    resolveOutcome("cancelled");

    verifyFn = verify;
    subjectKey = subject;
    activeMaxAttempts = Number(maxAttempts) > 0 ? Number(maxAttempts) : PIN_MAX_ATTEMPTS;
    activeLockoutMs = Number(lockoutMs) > 0 ? Number(lockoutMs) : PIN_LOCKOUT_MS;

    if (!loadPersistedState(subject)) {
      attemptsLeft.value = activeMaxAttempts;
      lockedUntil.value = 0;
    }
    secondsLeft.value = 0;
    justFailed.value = false;
    value.value = "";

    const outcome = new Promise((resolve) => {
      resultResolver = resolve;
    });

    if (lockedUntil.value > Date.now()) {
      status.value = "locked";
      startCountdown();
    } else {
      if (lockedUntil.value) {
        // expired lockout record — a fresh window starts now
        lockedUntil.value = 0;
        attemptsLeft.value = activeMaxAttempts;
        persistState();
      }
      status.value = "idle";
    }

    isOpen.value = true;
    return outcome;
  }

  // Called by the panel whenever the popup is dismissed by the user
  // (swipe, popup-close link, explicit cancel button).
  function cancel() {
    if (!resultResolver) return;
    closeWith("cancelled");
  }

  function close() {
    if (!resultResolver) return;
    closeWith("cancelled");
  }

  function append(digit) {
    if (status.value === "locked" || !resultResolver) return;
    if (value.value.length >= 4) return;
    justFailed.value = false;
    value.value = `${value.value}${String(digit)}`;
    if (value.value.length === 4) {
      submit();
    }
  }

  function backspace() {
    if (status.value === "locked" || !resultResolver) return;
    justFailed.value = false;
    value.value = value.value.slice(0, -1);
  }

  async function submit() {
    if (status.value === "locked" || !resultResolver) return;
    if (value.value.length < 4) return;
    if (typeof verifyFn !== "function") {
      closeWith("cancelled");
      return;
    }

    let ok = false;
    try {
      ok = await verifyFn(value.value);
    } catch (error) {
      console.error("PIN verification failed", error);
      ok = false;
    }

    value.value = "";

    if (ok) {
      attemptsLeft.value = activeMaxAttempts;
      persistState();
      closeWith("success");
      return;
    }

    attemptsLeft.value = Math.max(0, attemptsLeft.value - 1);
    if (attemptsLeft.value <= 0) {
      attemptsLeft.value = 0;
      lockedUntil.value = Date.now() + activeLockoutMs;
      status.value = "locked";
      justFailed.value = false;
      persistState();
      startCountdown();
      return;
    }

    justFailed.value = true;
    persistState();
  }

  return {
    isOpen,
    value,
    attemptsLeft,
    lockedUntil,
    secondsLeft,
    status,
    justFailed,
    open,
    cancel,
    close,
    append,
    backspace,
    submit,
  };
}